import React from 'react';
import { Box, Container, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const ProductsSection = styled(Box)`
  padding: 100px 0;
  position: relative;
  background: #000B18;
  overflow: hidden;
`;

const ProductCard = styled(motion.div)`
  background: rgba(0, 11, 24, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 229, 255, 0.1);
  border-radius: 24px;
  padding: 2rem;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(0, 229, 255, 0.3);
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);

    .product-image {
      transform: scale(1.05);
    }

    .vector-animation {
      transform: translateY(-5px);
      filter: drop-shadow(0 10px 20px rgba(0, 229, 255, 0.2));
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(180deg, rgba(0, 229, 255, 0.02) 0%, transparent 100%);
    pointer-events: none;
  }
`;

const ProductImage = styled(motion.div)`
  height: 200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  position: relative;

  .vector-animation {
    width: 180px;
    height: 180px;
    transition: all 0.3s ease;
  }

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ProductTitle = styled(motion.h3)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
`;

const HighlightedText = styled.span`
  color: #00e5ff;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #00e5ff, transparent);
    transform-origin: left;
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  ${ProductCard}:hover &::after {
    transform: scaleX(1);
  }
`;

const ProductDescription = styled(motion.p)`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin: 0;
`;

const AnimatedBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    linear-gradient(90deg, rgba(0, 229, 255, 0.01) 1px, transparent 1px),
    linear-gradient(0deg, rgba(0, 229, 255, 0.01) 1px, transparent 1px);
  background-size: 40px 40px;
  transform: perspective(1000px) rotateX(60deg) scale(6);
  transform-origin: center 0;
  opacity: 0.1;
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const OntractVector = () => (
  <svg viewBox="0 0 200 200" className="vector-animation">
    <defs>
      <linearGradient id="ontractGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00e5ff" />
        <stop offset="100%" stopColor="#2979ff" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <g fill="none" stroke="url(#ontractGradient)" strokeWidth="2" filter="url(#glow)">
      {/* Main document container */}
      <motion.rect
        x="40" y="40" width="120" height="120" rx="8"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
      />
      {/* Document lines with typing effect */}
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.line
          key={i}
          x1="60" y1={60 + i * 15} x2={140 - i * 10} y2={60 + i * 15}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 * i, repeat: Infinity, repeatType: "loop" }}
        />
      ))}
      {/* AI Processing circles */}
      <motion.circle
        cx="60" cy="170" r="8"
        initial={{ scale: 0.8, opacity: 0.5 }}
        animate={{ scale: 1.2, opacity: 1 }}
        transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
      />
      <motion.circle
        cx="85" cy="170" r="8"
        initial={{ scale: 0.8, opacity: 0.5 }}
        animate={{ scale: 1.2, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, repeat: Infinity, repeatType: "reverse" }}
      />
      <motion.circle
        cx="110" cy="170" r="8"
        initial={{ scale: 0.8, opacity: 0.5 }}
        animate={{ scale: 1.2, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4, repeat: Infinity, repeatType: "reverse" }}
      />
      {/* Smart verification seal */}
      <motion.circle
        cx="100" cy="40" r="20"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1.1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
      />
      <motion.path
        d="M90 40 L100 50 L110 40"
        initial={{ opacity: 0.5, strokeWidth: 1.5 }}
        animate={{ opacity: 1, strokeWidth: 3 }}
        transition={{ duration: 1, repeat: Infinity, repeatType: "reverse" }}
      />
    </g>
  </svg>
);

const AgriNovaVector = () => (
  <svg viewBox="0 0 200 200" className="vector-animation">
    <defs>
      <linearGradient id="agrinovaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00e5ff" />
        <stop offset="100%" stopColor="#2979ff" />
      </linearGradient>
      <filter id="agriGlow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <g fill="none" stroke="url(#agrinovaGradient)" strokeWidth="2" filter="url(#agriGlow)">
      {/* Outer circle */}
      <motion.circle
        cx="100" cy="100" r="50"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
      />

      {/* Middle circle */}
      <motion.circle
        cx="100" cy="100" r="35"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, delay: 0.5, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
      />

      {/* Inner circle */}
      <motion.circle
        cx="100" cy="100" r="20"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, delay: 1, repeat: Infinity, repeatType: "loop", ease: "easeInOut" }}
      />

      {/* Center point */}
      <motion.circle
        cx="100" cy="100" r="5"
        fill="url(#agrinovaGradient)"
        initial={{ scale: 0.8, opacity: 0.5 }}
        animate={{ scale: 1.2, opacity: 1 }}
        transition={{ duration: 1, repeat: Infinity, repeatType: "reverse" }}
      />
    </g>
  </svg>
);

const MilanVivahVector = () => (
  <svg viewBox="0 0 200 200" className="vector-animation">
    <defs>
      <linearGradient id="milanGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00e5ff" />
        <stop offset="100%" stopColor="#2979ff" />
      </linearGradient>
      <filter id="milanGlow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <g fill="none" stroke="url(#milanGradient)" strokeWidth="2" filter="url(#milanGlow)">
      {/* First heart */}
      <motion.path
        d="M70 120 L50 100 C30 80 50 60 70 80 C90 60 110 80 90 100 L70 120"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
      />

      {/* Second heart */}
      <motion.path
        d="M130 120 L110 100 C90 80 110 60 130 80 C150 60 170 80 150 100 L130 120"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2, delay: 0.5, repeat: Infinity, repeatType: "loop" }}
      />
    </g>
  </svg>
);

const Products = () => {
  const products = [
    {
      id: 1,
      title: 'Ontract.AI',
      highlight: 'revolutionary',
      description: 'contract management platform harnesses advanced NLP and machine learning to transform how organizations create, analyze, and manage legal agreements. It understands contracts, identifies risks, ensures compliance, and extracts business intelligence.',
      VectorComponent: OntractVector
    },
    {
      id: 2,
      title: 'AgriNova',
      highlight: 'comprehensive',
      description: 'farm digitization ecosystem that combines IoT sensors, satellite imagery, and machine learning to revolutionize agricultural operations. AgriNova provides unprecedented visibility into farm operations while delivering actionable recommendations that maximize yield, minimize resource usage, and ensure sustainability.',
      VectorComponent: AgriNovaVector
    },
    {
      id: 3,
      title: 'Milan Vivah',
      highlight: 'unique',
      description: 'social platform leverages relationship analytics and cultural understanding to create meaningful connections within tribal communities. This purpose-built solution demonstrates our commitment to applying technology for social good.',
      VectorComponent: MilanVivahVector
    }
  ];

  return (
    <ProductsSection id="products">
      <AnimatedBackground
        animate={{
          backgroundPosition: ['0px 0px', '40px 40px']
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }}
      />
      <Container maxWidth="lg">
        <SectionTitle>Our Products</SectionTitle>
        <SectionSubtitle>
          Discover our innovative solutions that combine cutting-edge technology with practical applications,
          designed to transform industries and improve lives.
        </SectionSubtitle>
        <Grid container spacing={4}>
          {products.map((product, index) => (
            <Grid item xs={12} md={4} key={product.id}>
              <ProductCard
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <ProductImage className="product-image">
                  <product.VectorComponent />
                </ProductImage>
                <ProductTitle>
                  {product.title}
                </ProductTitle>
                <ProductDescription>
                  Our <HighlightedText>{product.highlight}</HighlightedText> {product.description}
                </ProductDescription>
              </ProductCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </ProductsSection>
  );
};

export default Products;